import React, { useState } from "react";
import { IconButton, ListItemIcon, Menu, MenuItem, TableCell } from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  DoorOpenIcon,
  DotsThreeIcon,
  FilesIcon,
  PaperPlaneTiltIcon,
  PasswordIcon,
  UserCircleIcon,
} from "@phosphor-icons/react";
import { useResetUserPassword } from "hooks/api/useResetUserPassword";
import { useGlobalToast } from "components/GlobalToastProvider";

interface ActionsMenuProps {
  row: {
    userId?: string | number;
    hasAcceptedInvite: boolean;
    isDesignatedCaregiver: boolean;
    isLegalGuardian: boolean;
    expirationDate?: string | Date | null;
  };
  onRemoveMember: () => void;
  onEditProfile: () => void;
  onResendInvite: () => void;
  onCreateROI: () => void;
  userPermissions?: {
    create_phi_release_request?: boolean;
  };
}

interface MenuItem {
  label: string;
  value: string;
  icon: React.ReactNode;
  action: () => void;
  disabled?: boolean;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  row,
  onRemoveMember,
  onEditProfile,
  onResendInvite,
  onCreateROI,
  userPermissions,
}) => {
  const theme: any = useTheme();
  const { showToast } = useGlobalToast();
  const [anchorOptionsEl, setAnchorOptionsEl] = useState<null | HTMLElement>(null);
  const openOptions = Boolean(anchorOptionsEl);
  const handleOptionsClose = () => setAnchorOptionsEl(null);
  const { resetUserPasswordMutation, isLoading } = useResetUserPassword();

  const iconProps = { color: theme.palette.action.active, weight: "duotone" as const };
  const optionsMenuItems: MenuItem[] = [
    {
      label: "Edit Profile",
      value: "edit_profile",
      icon: <UserCircleIcon {...iconProps} />,
      action: onEditProfile,
    },
    ...(!row.isDesignatedCaregiver && !row.isLegalGuardian
      ? [
          {
            label: "Create Release of Information Form",
            value: "create_roi",
            icon: <FilesIcon {...iconProps} />,
            disabled:
              !userPermissions?.create_phi_release_request ||
              !row.userId,
            action: onCreateROI,
          },
        ]
      : []),
    {
      label: "Send Password Reset Link",
      value: "send_password_reset_link",
      icon: <PasswordIcon {...iconProps} />,
      disabled: isLoading || !row.userId,
      action: () => {
        if (!row.userId) return;
        resetUserPasswordMutation(row.userId, {
          onSuccess: () => showToast({ message: "The link has been successfully sent!" }),
        });
      },
    },
    {
      label: "Remove from Team",
      value: "remove_from_team",
      icon: <DoorOpenIcon {...iconProps} />,
      disabled: row.isDesignatedCaregiver,
      action: onRemoveMember,
    },
    {
      label:
        row.expirationDate && new Date(row.expirationDate) < new Date()
          ? "Send Invite"
          : "Resend Invite",
      value: "resend_invite",
      icon: <PaperPlaneTiltIcon {...iconProps} />,
      action: onResendInvite,
    },
  ];

  return (
    <>
      <Menu
        id="options-menu"
        anchorEl={anchorOptionsEl}
        open={openOptions}
        onClose={handleOptionsClose}
        MenuListProps={{ "aria-labelledby": "open-options" }}
      >
        {optionsMenuItems.map((item) => {
          if (!row.hasAcceptedInvite && item.value === "send_password_reset_link") return null;
          if (row.hasAcceptedInvite && item.value === "resend_invite") return null;
          return (
            <MenuItem
              key={`${item.value}`}
              value={item.value}
              onClick={() => {
                item.action();
                handleOptionsClose();
              }}
              disabled={item.disabled}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              {item.label}
            </MenuItem>
          );
        })}
      </Menu>
      <TableCell align="right" sx={{ width: "120px", p: 4 }}>
        <IconButton
          id="open-options"
          aria-label="Open actions menu"
          size="small"
          onClick={(e) => setAnchorOptionsEl(e.currentTarget)}
          aria-controls={openOptions ? "options-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openOptions ? "true" : undefined}
        >
          <DotsThreeIcon {...iconProps} />
        </IconButton>
      </TableCell>
    </>
  );
};

export default ActionsMenu;
